(() => {
  /*
   * Beelite Candles - Automatic Free Beeswax Pearl
   *
   * QUALIFYING RULE:
   * Any cart product tagged "Candle Mold".
   *
   * PEARL:
   * Normal $5.95 Pearl variant.
   *
   * Shopify's Buy X Get Y discount is responsible
   * for making ONE Pearl free.
   */

  const CONFIG = {
    pearlVariantId: 46070620455150,
    qualifyingTag: 'Candle Mold',
    giftProperty: '_beelite_free_sample',
    declinedStorageKey: 'beelite_free_sample_declined'
  };

  const shopRoot = window.Shopify?.routes?.root || '/';

  const productCache = new Map();

  let syncing = false;

  /*
   * Tracks whether ANY Pearl was present during the
   * previous cart state.
   *
   * This lets us recognize when the customer removes
   * the Pearl while a qualifying mold remains.
   */
  let pearlPreviouslyPresent = false;


  /* ---------------------------------------------
     GET CART
  --------------------------------------------- */

  async function getCart() {
    const response = await fetch(`${shopRoot}cart.js`, {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to retrieve Shopify cart.');
    }

    return response.json();
  }


  /* ---------------------------------------------
     GET PRODUCT DATA
  --------------------------------------------- */

  async function getProduct(handle) {
    if (productCache.has(handle)) {
      return productCache.get(handle);
    }

    const response = await fetch(
      `${shopRoot}products/${encodeURIComponent(handle)}.js`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Unable to retrieve product: ${handle}`);
    }

    const product = await response.json();

    productCache.set(handle, product);

    return product;
  }


  /* ---------------------------------------------
     CHECK FOR QUALIFYING MOLD
  --------------------------------------------- */

  async function isQualifyingProduct(item) {

    /*
     * The Pearl itself can NEVER qualify,
     * regardless of its tags.
     */
    if (
      Number(item.variant_id) ===
      Number(CONFIG.pearlVariantId)
    ) {
      return false;
    }

    try {
      const product = await getProduct(item.handle);

      const tags = Array.isArray(product.tags)
        ? product.tags
        : [];

      return tags.some(
        tag =>
          String(tag).trim().toLowerCase() ===
          CONFIG.qualifyingTag.toLowerCase()
      );

    } catch (error) {

      console.error(
        '[Beelite Free Sample] Unable to check product tags:',
        item.handle,
        error
      );

      return false;
    }
  }


  async function cartQualifies(cart) {
    const results = await Promise.all(
      cart.items.map(isQualifyingProduct)
    );

    return results.some(Boolean);
  }


  /* ---------------------------------------------
     FIND PEARLS
  --------------------------------------------- */

  function getAllPearlLines(cart) {
    return cart.items.filter(
      item =>
        Number(item.variant_id) ===
        Number(CONFIG.pearlVariantId)
    );
  }


  /*
   * Only Pearl lines that OUR SCRIPT created.
   */
  function getAutomaticGiftLines(cart) {
    return getAllPearlLines(cart).filter(
      item =>
        item.properties &&
        item.properties[CONFIG.giftProperty] === 'true'
    );
  }


  /* ---------------------------------------------
     DECLINED SAMPLE
  --------------------------------------------- */

  function sampleWasDeclined() {
    return (
      sessionStorage.getItem(
        CONFIG.declinedStorageKey
      ) === 'true'
    );
  }


  function markSampleDeclined() {
    sessionStorage.setItem(
      CONFIG.declinedStorageKey,
      'true'
    );
  }


  function clearSampleDeclined() {
    sessionStorage.removeItem(
      CONFIG.declinedStorageKey
    );
  }


  /* ---------------------------------------------
     ADD AUTOMATIC PEARL
  --------------------------------------------- */

  async function addGift() {
    const response = await fetch(`${shopRoot}cart/add.js`, {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },

      body: JSON.stringify({
        items: [
          {
            id: CONFIG.pearlVariantId,
            quantity: 1,

            properties: {
              [CONFIG.giftProperty]: 'true'
            }
          }
        ]
      })
    });

    if (!response.ok) {
      throw new Error('Unable to add free sample.');
    }

    return response.json();
  }


  /* ---------------------------------------------
     CHANGE / REMOVE LINE
  --------------------------------------------- */

  async function changeLine(lineKey, quantity) {
    const response = await fetch(`${shopRoot}cart/change.js`, {
      method: 'POST',

      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },

      body: JSON.stringify({
        id: lineKey,
        quantity: quantity
      })
    });

    if (!response.ok) {
      throw new Error('Unable to update free sample.');
    }

    return response.json();
  }


  /* ---------------------------------------------
     REFRESH PRESTIGE CART
  --------------------------------------------- */

  function refreshPrestigeCart() {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true
      })
    );
  }


  /* ---------------------------------------------
     MAIN LOGIC
  --------------------------------------------- */

  async function syncFreeSample(existingCart = null) {

    if (syncing) return;

    syncing = true;

    try {
      const cart = existingCart || await getCart();

      const qualifies = await cartQualifies(cart);

      const allPearls = getAllPearlLines(cart);

      const automaticGifts =
        getAutomaticGiftLines(cart);

      const pearlExists =
        allPearls.length > 0;

      let changed = false;


      /*
       * CUSTOMER REMOVED THE PEARL
       *
       * If the cart still qualifies, a Pearl used to
       * exist, and now NO Pearl exists, respect that.
       */
      if (
        qualifies &&
        pearlPreviouslyPresent &&
        !pearlExists
      ) {
        markSampleDeclined();
      }


      /* -----------------------------------------
         NO QUALIFYING MOLDS
      ----------------------------------------- */

      if (!qualifies) {

        /*
         * Reset decline preference for the next
         * qualifying mold purchase.
         */
        clearSampleDeclined();


        /*
         * Remove ONLY Pearls that this script added.
         *
         * Manually purchased $5.95 Pearls remain.
         */
        if (automaticGifts.length > 0) {

          for (const gift of automaticGifts) {
            await changeLine(gift.key, 0);
          }

          changed = true;
        }


        /*
         * Manually purchased Pearl still counts
         * as being present.
         */
        const manualPearlExists =
          allPearls.some(
            item =>
              !(
                item.properties &&
                item.properties[CONFIG.giftProperty] === 'true'
              )
          );

        pearlPreviouslyPresent =
          manualPearlExists;


        if (changed) {
          refreshPrestigeCart();
        }

        return;
      }


      /* -----------------------------------------
         CART QUALIFIES
      ----------------------------------------- */

      /*
       * HERE IS THE IMPORTANT CHANGE:
       *
       * If ANY Pearl already exists in the cart,
       * DO NOT add another one.
       *
       * Shopify's Buy X Get Y discount can simply
       * make the existing Pearl free.
       */
      if (
        qualifies &&
        !pearlExists &&
        !sampleWasDeclined()
      ) {
        await addGift();

        pearlPreviouslyPresent = true;

        changed = true;
      }


      /*
       * If our automatic gift exists, make sure
       * its quantity stays at exactly one.
       *
       * This does NOT touch manually added Pearls.
       */
      if (automaticGifts.length > 0) {

        const gift = automaticGifts[0];

        if (gift.quantity !== 1) {
          await changeLine(
            gift.key,
            1
          );

          changed = true;
        }


        /*
         * Defensive cleanup if somehow multiple
         * automatic gift lines exist.
         */
        if (automaticGifts.length > 1) {

          for (
            let i = 1;
            i < automaticGifts.length;
            i++
          ) {
            await changeLine(
              automaticGifts[i].key,
              0
            );
          }

          changed = true;
        }
      }


      if (pearlExists) {
        pearlPreviouslyPresent = true;
      }


      if (changed) {
        refreshPrestigeCart();
      }

    } catch (error) {

      console.error(
        '[Beelite Free Sample]',
        error
      );

    } finally {

      syncing = false;

    }
  }


  /* ---------------------------------------------
     PRESTIGE CART EVENTS
  --------------------------------------------- */

  document.addEventListener(
    'cart:change',
    event => {
      syncFreeSample(
        event.detail?.cart || null
      );
    }
  );


  /* ---------------------------------------------
     INITIAL PAGE LOAD
  --------------------------------------------- */

  async function initialize() {
    try {
      const cart = await getCart();

      pearlPreviouslyPresent =
        getAllPearlLines(cart).length > 0;

      await syncFreeSample(cart);

    } catch (error) {

      console.error(
        '[Beelite Free Sample]',
        error
      );
    }
  }


  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      initialize
    );

  } else {

    initialize();

  }

})();